import updateEmojiSelectedIndex from '../actions/updateEmojiSelectedIndex';
import updateInlineSearch from '../actions/updateInlineSearch';
import { EMOJI_PICKER_TRIGGER_CHARACTER } from '../constants';
import AddEmojiMethod from '../store/schema/AddEmojiMethod';
import EmojiPickerViewState from '../store/schema/EmojiPickerViewState';
import getViewAllIndex from '../utils/getViewAllIndex';
import { logUsage } from 'owa-analytics';
import { EditorViewState, PluginViewStateAdapter } from 'owa-editor';
import setIsSuggesting from 'owa-editor-picker-plugin/lib/actions/setIsSuggesting';
import setTargetPoint from 'owa-editor-picker-plugin/lib/actions/setTargetPoint';
import { lazyCloseExpressionPane } from 'owa-expressions-store';
import { INLINE_SEARCH_METHOD } from 'owa-expressions-store/lib/store/schema/GifSearchMethod';
import addEmoji from 'owa-expressions-store/lib/utils/addEmoji';
import getEmoji from 'owa-expressions-store/lib/utils/getEmoji';
import getOrInitExpressionPickerViewState from 'owa-expressions-store/lib/utils/getOrInitExpressionPickerViewState';
import matchGif from 'owa-expressions-store/lib/utils/matchGif';
import { lazyShowExpressionPane } from 'owa-expressions-view';
import { isFeatureEnabled } from 'owa-feature-flags';
import { Editor } from 'roosterjs-editor-core';
import { PickerDataProvider } from 'roosterjs-plugin-picker/lib/PickerDataProvider';
import matchEmojiShortcut, {
    isPossibleEmojiShortcutEnd,
} from 'owa-expressions-store/lib/utils/matchEmojiShortcut';

const EMOJI_SEARCH_DELAY = 300;
const EMOJI_SHORTCODE_END = ':';
const RESIZE_EVENT_NAME = 'resize';

export default class EmojiPluginDataProvider implements PickerDataProvider {
    private timer: NodeJS.Timer;
    private insertNode: (element: HTMLElement) => void;
    private setIsSuggesting: (isSuggesting: boolean) => void;
    private targetWindow: Window;

    constructor(
        private adapter: PluginViewStateAdapter<EmojiPickerViewState>,
        private editorAdapter: PluginViewStateAdapter<EditorViewState>,
        private useFlexPane: boolean,
        private expressionId: string
    ) {}

    onInitalize(
        insertNodeCallback: (element: HTMLElement) => void,
        isSuggestingCallback: (isSuggesting: boolean) => void,
        editor: Editor
    ) {
        window.addEventListener(RESIZE_EVENT_NAME, this.onScroll);
        this.insertNode = insertNodeCallback;
        this.setIsSuggesting = isSuggestingCallback;
        this.targetWindow = editor?.getDocument()?.defaultView;
    }

    onDispose() {
        lazyCloseExpressionPane.importAndExecute(this.expressionId, this.targetWindow);
        window.removeEventListener(RESIZE_EVENT_NAME, this.onScroll);
        this.resetTimer();
    }

    onIsSuggestingChanged = (isSuggesting: boolean) => {
        setIsSuggesting(this.adapter(), isSuggesting);
        if (!isSuggesting) {
            setTargetPoint(this.adapter(), null /*targetPoint*/, 0 /*buffer/Zone*/);
        }
    };

    queryStringUpdated = (queryString: string, isExactMatch: boolean) => {
        const viewState = this.adapter();
        const { isSuggesting, emojiResults } = viewState;

        if (isSuggesting) {
            const queryLen = queryString.length;
            if (queryLen <= 0) {
                this.handleInlineEmojiSearch(viewState);
            } else {
                // Check the last character of the query string, auto-complete if shortcode or shortcut
                const lastChar = queryString.charAt(queryLen - 1);
                if (lastChar === EMOJI_SHORTCODE_END) {
                    const queryKey = queryString.substring(0, queryLen - 1).toLowerCase();
                    for (let emojiResult of emojiResults) {
                        // Searches emoji results in inline picker for a match
                        const emoji = getEmoji(emojiResult);
                        if (queryKey === emoji.description.toLowerCase()) {
                            this.insertEmoji(emoji.key, 'shortcode');
                            return;
                        }
                    }
                } else if (isExactMatch && isPossibleEmojiShortcutEnd(lastChar)) {
                    // Handle shortcuts when the EmojiPickerResult is open, i.e. : has been typed
                    const emojiResultKey = matchEmojiShortcut(
                        EMOJI_PICKER_TRIGGER_CHARACTER + queryString
                    );
                    if (emojiResultKey) {
                        this.insertEmoji(emojiResultKey, 'shortcut');
                        return;
                    }
                }

                // Otherwise, clear the timer and queue a new emoji search
                this.resetTimer();
                this.timer = setTimeout(() => {
                    if (viewState.isSuggesting) {
                        // Only process emoji search when the popup is up and suggesting
                        this.handleInlineEmojiSearch(viewState, queryString);
                    }
                    this.timer = null;
                }, EMOJI_SEARCH_DELAY);
            }
        }
    };

    selectOption = () => {
        const { selectedEmojiIndex, emojiResults } = this.adapter();

        if (selectedEmojiIndex < getViewAllIndex(emojiResults)) {
            // Emoji option
            const emojiKey = emojiResults[selectedEmojiIndex];
            this.insertEmoji(emojiKey, 'inlinePicker');
        } else {
            // View all (...) option
            this.openExpressionPicker();
        }
    };

    onRemove = (nodeRemoved: Node, isBackspace: boolean) => {
        return null;
    };

    setCursorPoint = (targetPoint: { x: number; y: number }, buffer: number) => {
        setTargetPoint(this.adapter(), targetPoint, buffer);
        logUsage('ExpressionPicker_EmojiInlinePickerOpened');
    };

    shiftHighlight = (isDown: boolean) => {
        const amount = isDown ? 1 : -1;
        updateEmojiSelectedIndex(this.adapter(), amount);
    };

    getSelectedIndex = (): number => {
        return this.adapter().selectedEmojiIndex;
    };

    onScroll = () => {
        if (this.adapter().isSuggesting) {
            this.setIsSuggesting(false);
        }
    };

    private resetTimer() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    private insertEmoji = (emojiKey: string, method: AddEmojiMethod) => {
        const emojiNode = addEmoji(
            emojiKey,
            this.insertNode,
            'inlineSearch' /* source */,
            this.expressionId
        );
        if (emojiNode) {
            // Log EmojiInsertedFromInlinePicker datapoint
            logUsage('ExpressionPicker_EmojiInsertedFromInlinePicker', [
                emojiNode.id /* codepoint */,
                this.adapter().queryString !== '' /* isSearch */,
                method,
            ]);

            this.setIsSuggesting(false);
        }
    };

    private openExpressionPicker = () => {
        lazyShowExpressionPane.importAndExecute(
            this.useFlexPane,
            this.adapter().queryString,
            'inlineSearch',
            this.editorAdapter,
            this.expressionId,
            this.targetWindow
        );

        this.setIsSuggesting(false);
    };

    private handleInlineEmojiSearch = async (
        viewState: EmojiPickerViewState,
        queryString?: string
    ) => {
        // The expression picker needs to be initialized here as it is required
        // for GIF search (VSO 61829)
        const expressionPickerViewState = await getOrInitExpressionPickerViewState(
            this.expressionId
        );

        updateInlineSearch(viewState, queryString);

        const emojiResults = viewState.emojiResults;
        const gifResults =
            isFeatureEnabled('cmp-gifPicker') && queryString
                ? await matchGif(
                      expressionPickerViewState.gifPickerViewState,
                      queryString,
                      INLINE_SEARCH_METHOD
                  )
                : null;
        const hasSearchResults = emojiResults?.length > 0 || gifResults?.length > 0;
        this.setIsSuggesting(hasSearchResults);
    };
}
